import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const SupportersCarousel = ({ supporters }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemsPerView = 6;
  const totalSlides = supporters ? Math.ceil(supporters.length / itemsPerView) : 0;

  useEffect(() => {
    if (!supporters || supporters.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 4000);
    return () => clearInterval(interval);
  }, [totalSlides, supporters]);

  if (!supporters || supporters.length === 0) return null;

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const visibleSupporters = supporters.slice(
    currentIndex * itemsPerView,
    (currentIndex + 1) * itemsPerView
  );

  return (
    <section className="py-16 bg-slate-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <h3 className="text-3xl font-bold text-white mb-3">
            Our <span className="text-[var(--madder)]">Supporters</span>
          </h3>
          <p className="text-gray-400">
            Thank you to our community supporters
          </p>
        </div>

        <div className="relative">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {visibleSupporters.map((supporter) => (
              <div
                key={supporter.id}
                className="bg-white/5 backdrop-blur-sm rounded-lg overflow-hidden border border-white/10 hover:border-[var(--madder)]/50 hover:shadow-lg transition-all duration-300 group cursor-pointer transform hover:scale-105"
                onClick={() => window.open(supporter.link, '_blank')}
              >
                <div className="relative h-24 bg-gradient-to-br from-slate-700 to-slate-800 overflow-hidden">
                  <img
                    src={supporter.logo}
                    alt={supporter.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => { e.target.src = '/api/files/paisley-org/wp-content/uploads/2025/09/Paisley-skyline-from-abbey-770x513-1.jpg'; }}
                  />
                  <div className="absolute inset-0 bg-[var(--madder)]/0 group-hover:bg-[var(--madder)]/10 transition-colors duration-300"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-3 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={prevSlide}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 backdrop-blur-sm"
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={nextSlide}
              className="bg-white/5 border-white/10 text-white hover:bg-white/10 backdrop-blur-sm"
            >
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-1.5 mt-4">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-6 bg-cyan-400'
                    : 'w-1.5 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupportersCarousel;
